/**
 * Web component: Flip clock (flip-clock)
 * -----------------------------------------------------------------------------
 * Flip Clock to count down to set end date and hide given elements.
 *
 */
/* eslint no-unused-vars: ['error', { 'argsIgnorePattern': 'component' }] */
import './flip-clock.scss';

import { vueReady } from '~/core/runners';
import classes from '~/config/classes';

/**
 * DOM selectors.
 */
const selectors = {
  countdownCard: '[js-flip-clock="countdownCard"]',
  countdown: '[js-flip-clock="countdown"]',
  daysCardsWrapper: '[js-flip-clock="days"]',
  hoursCardsWrapper: '[js-flip-clock="hours"]',
  minutesCardsWrapper: '[js-flip-clock="minutes"]',
  secondsCardsWrapper: '[js-flip-clock="seconds"]',
};

/**
 * Create new <flip-clock> custom element.
 */
class FlipClock extends HTMLElement {
  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.countdownTimer();
  }

  /**
   * Get time remaining until end time.
   * @param {String} end_time - End time.
   * @returns {Object} - Time remaining.
   *
   */
  getTimeRemaining(endTime) {
    const remainingTime = Date.parse(endTime) - Date.parse(new Date());
    return {
      total: remainingTime,
      days: Math.floor(remainingTime / (1000 * 60 * 60 * 24)),
      hours: Math.floor((remainingTime / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((remainingTime / 1000 / 60) % 60),
      seconds: Math.floor((remainingTime / 1000) % 60),
    };
  }

  /**
   * Handle countdown timer.
   */
  countdownTimer() {
    const countdownElement = this.querySelector(selectors.countdown);
    const refreshSite = countdownElement.dataset.refreshSite;
    const countdownUnits = [
      { unit: 'days', selector: selectors.daysCardsWrapper },
      { unit: 'hours', selector: selectors.hoursCardsWrapper },
      { unit: 'minutes', selector: selectors.minutesCardsWrapper },
      { unit: 'seconds', selector: selectors.secondsCardsWrapper },
    ];
    const endTime = countdownElement.dataset.countdownDate;
    const card = this.querySelector(selectors.countdownCard);
    const cards = this.querySelectorAll(selectors.countdownCard);
    const countdownInterval = setInterval(() => {
      const countdown = this.getTimeRemaining(endTime);

      if (countdown.total < 0) {
        clearInterval(countdownInterval);
        this.hideCampaignInfoOrCountdown('countdown');

        if (card.classList.contains('is-loading')) {
          cards.forEach((elem) => elem.classList.remove('is-loading'));
        }
        return;
      }

      if (refreshSite === 'true' && countdown.total <= 0) {
        clearInterval(countdownInterval);
        location.reload();
        return;
      }

      if (card.classList.contains('is-loading')) {
        cards.forEach((elem) => elem.classList.remove('is-loading'));
      }

      this.updateCountdown(countdownElement, countdownUnits, endTime);
    }, 1000);
  }

  /**
   * Update countdown timer.
   * @param {Element} countdownElement - Countdown element.
   * @param {Array} countdownUnits - Countdown units.
   * @param {String} endTime - End time.
   */
  updateCountdown(countdownElement, countdownUnits, endTime) {
    const countdown = this.getTimeRemaining(endTime);
    const showDigits = countdownElement.dataset.showDigits;
    let countShowingDigits = 0;

    countdownUnits.forEach(({ unit, selector }) => {
      const countdownUnit = countdownElement.querySelector(selector);

      let continueCondition = false;
      switch (unit) {
        case 'minutes':
        case 'seconds':
          continueCondition =
            countdownUnit !== null && countShowingDigits < showDigits;
          break;
        case 'hours':
          continueCondition =
            countdownUnit !== null &&
            (countdown.days >= 1 || countdown.hours > 0) &&
            countShowingDigits < 2;
          break;
        default:
          continueCondition =
            countdownUnit !== null &&
            countdown[unit] > 0 &&
            countShowingDigits < 2;
      }

      if (continueCondition) {
        const formattedValue = `0${countdown[unit]}`.slice(-2);
        countdownUnit.querySelectorAll(selectors.countdownCard)[0].innerText =
          formattedValue[0];
        countdownUnit.querySelectorAll(selectors.countdownCard)[1].innerText =
          formattedValue[1];
        countdownUnit.parentElement.classList.remove(classes.hidden);
        countShowingDigits++;
      } else if (countdownUnit !== null) {
        countdownUnit.parentElement.classList.add(classes.hidden);
      }
    });
  }

  /**
   * Show/hide campaign elements after countdown ends.
   * @param {String} hideContentType - Hide content options
   * type 'countdown' or 'campaign'.
   */
  hideCampaignInfoOrCountdown(hideContentType) {
    const countdownWrapper = this;
    const teaserInfo = document.querySelectorAll(this.dataset.toggleClasses);

    if (hideContentType === 'countdown') {
      countdownWrapper.classList.add(classes.hidden);

      teaserInfo.forEach((element) => {
        element.classList.remove(classes.hidden);
      });
    } else if (hideContentType === 'campaign') {
      teaserInfo.forEach((element) => {
        element.classList.add(classes.hidden);
      });

      countdownWrapper.classList.remove(classes.hidden);
    }
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('flip-clock')) {
    return;
  }

  customElements.define('flip-clock', FlipClock);
});
